/**
 * FolderGrid Component
 * Responsive grid of folder cards with create and all-photos action tiles
 * Shows loading skeletons and empty state
 *
 * @module components/gallery/FolderGrid
 */

import React, { useEffect } from 'react';
import { Album, User } from '@/types';
import { logger } from '@/utils/logger';
import { Skeleton } from '@/components/common';
import FolderCard from './FolderCard';

// ============================================================================
// TYPES
// ============================================================================

interface FolderGridProps {
  albums: Album[];
  currentUser?: User | null;
  onFolderClick: (album: Album) => void;
  onCreateFolder: () => void;
  onViewAllPhotos: () => void;
  isLoading?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Folder grid component displaying albums as folder tiles
 * Includes "Create New Folder" and "All Photos" action tiles
 * Responsive grid layout matching PhotoGrid conventions
 *
 * @component
 * @example
 * <FolderGrid
 *   albums={albums}
 *   currentUser={user}
 *   onFolderClick={handleFolderClick}
 *   onCreateFolder={handleCreateFolder}
 *   onViewAllPhotos={handleViewAll}
 *   isLoading={isLoading}
 * />
 */
const FolderGrid: React.FC<FolderGridProps> = ({
  albums,
  currentUser,
  onFolderClick,
  onCreateFolder,
  onViewAllPhotos,
  isLoading = false,
}) => {
  // Debug logging
  useEffect(() => {
    logger.debug(`[WALRUS] FolderGrid: mounted`, {
      albumCount: albums.length,
      isLoading,
    });
  }, [albums.length, isLoading]);

  // Render loading skeleton grid
  if (isLoading) {
    logger.debug(`[WALRUS] FolderGrid: rendering loading state`);
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={`skeleton-${index}`} className="rounded-lg overflow-hidden bg-walrus-cream border border-walrus-gray">
            <Skeleton
              width="100%"
              height="160px"
              rounded
              className="aspect-video"
            />
            <div className="p-3 space-y-2">
              <Skeleton width="60%" height="14px" rounded />
            </div>
          </div>
        ))}
      </div>
    );
  }

  logger.debug(`[WALRUS] FolderGrid: rendering ${albums.length} folders`);

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {/* Create New Folder tile - always first */}
      <div
        className="group relative flex flex-col items-center justify-center overflow-hidden rounded-lg border-2 border-dashed border-walrus-gray bg-walrus-white transition-all duration-200 hover:border-walrus-black hover:shadow-lg cursor-pointer aspect-video"
        onClick={onCreateFolder}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onCreateFolder();
          }
        }}
      >
        <svg
          className="h-10 w-10 text-walrus-silver transition-colors duration-200 group-hover:text-walrus-black"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 4.5v15m7.5-7.5h-15"
          />
        </svg>
        <p className="mt-2 text-sm font-medium text-walrus-silver transition-colors duration-200 group-hover:text-walrus-black">
          Create New Folder
        </p>
      </div>

      {/* Album folder cards */}
      {albums.map((album) => (
        <FolderCard
          key={album.id}
          album={album}
          currentUser={currentUser}
          onClick={onFolderClick}
        />
      ))}

      {/* All Photos tile - always last */}
      <div
        className="group relative flex flex-col items-center justify-center overflow-hidden rounded-lg border border-walrus-gray bg-walrus-cream transition-all duration-200 hover:border-walrus-black hover:shadow-lg cursor-pointer aspect-video"
        onClick={onViewAllPhotos}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onViewAllPhotos();
          }
        }}
      >
        <svg
          className="h-10 w-10 text-walrus-silver transition-colors duration-200 group-hover:text-walrus-black"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <p className="mt-2 text-sm font-medium text-walrus-silver transition-colors duration-200 group-hover:text-walrus-black">
          All Photos
        </p>
      </div>
    </div>
  );
};

FolderGrid.displayName = 'FolderGrid';

export default FolderGrid;
